// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import React, {Fragment, useEffect, useRef, useState} from 'react';
import cn from 'classnames';
import {Basic_input, i_basic_input_props, t_content,
    t_render_prop} from './basic_input';
import Icon_warning from './icons/warning.svg';
import Icon_question from './icons/question.svg';
import Icon_eye from './icons/eye.svg';
import Icon_eye_crossed from './icons/eye_crossed.svg';
import Icon_copy from './icons/copy.svg';
import Icon_check from './icons/check.svg';
import Icon_x from './icons/x.svg';
import Icon_search from './icons/search.svg';
import {Basic_select} from '../select';
import {i_basic_select_option} from '../select/basic_select';
import {get_config} from '../select/select_config';
import etask from 'zetask';

import styles from './input.module.less';

export const default_icons = {
    warning: <Icon_warning/>,
    hint: <Icon_question/>,
    pass_shown: <Icon_eye/>,
    pass_hidden: <Icon_eye_crossed/>,
    x_clear: <Icon_x/>,
    search: <Icon_search/>,
};

export const set_default_icons = (icons: Partial<typeof default_icons>)=>{
    Object.assign(default_icons, icons);
};

interface i_text_input_props extends i_basic_input_props {
    error?: t_content;
    hint?: t_content;
    error_icon?: JSX.Element;
    hint_icon?: JSX.Element;
    with_hint?: boolean;
    input_prefix?: string;
    button?: JSX.Element;
    disabled?: boolean;
    search_icon?: boolean;
    x_icon?: boolean;
    on_clear?: ()=>void;
}
export const Text_input = (props: i_text_input_props)=>{
    const {is_error, error, hint, with_hint, slot_left,
        error_icon = default_icons.warning,
        hint_icon = default_icons.hint,
        ...rest_props} = props;
    const has_error = !!error;
    const has_error_icon = is_error && !!error_icon;
    const has_hint = with_hint && !is_error && !has_error && !!hint;
    const has_hint_icon = with_hint && !has_error_icon && !!hint_icon;
    const search_slot = props.search_icon ?
        <div className={styles.search_icon}>{default_icons.search}</div> :
        slot_left;
    return <Basic_input
      {...rest_props}
      is_error={is_error}
      cls={cn(styles.text_input, props.cls,
        props.input_prefix && styles.with_prefix)}
      slot_left={props.input_prefix
        ? <span className={styles.input_prefix}>{props.input_prefix}</span>
        : search_slot}
      slot_right={<Fragment>
        {has_error_icon && <div className={styles.error_icon}>
          {error_icon}</div>}
        {has_hint_icon && <div className={styles.hint_icon}>
          {hint_icon}</div>}
        {props.button && <div className={styles.button_container}>
          {props.button}</div>}
        {props.x_icon && <div className={styles.x_icon}
          onClick={props.on_clear}>{default_icons.x_clear}</div>}
      </Fragment>}
      slot_bottom={props.slot_bottom || <Fragment>
        {has_error && <div className={styles.error}>{error}</div>}
        {has_hint && <div className={styles.hint}>{hint}</div>}
      </Fragment>}
    />;
};
interface i_text_input_props_with_copy extends i_text_input_props {
  prevent_default_copy?: boolean;
  copy_icon_cls?: string;
  on_copy?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>)=>void;
}
export const Text_input_with_copy = (props: i_text_input_props_with_copy)=>{
    const {is_error, error, hint, with_hint,
        error_icon = default_icons.warning,
        hint_icon = default_icons.hint,
        prevent_default_copy,
        on_copy = e=>console.error('on_copy not set', e),
        ...rest_props} = props;
    const has_error = !!error;
    const has_error_icon = is_error && !!error_icon;
    const has_hint = with_hint && !is_error && !has_error && !!hint;
    const has_hint_icon = with_hint && !has_error_icon && !!hint_icon;
    const copy_timeout = useRef<NodeJS.Timeout>();
    useEffect(()=>()=>clearTimeout(copy_timeout.current), []);
    const [copied, set_copied] = useState(false);
    const handle_copy = ()=>{
        if (typeof props.value != 'string')
            return;
        etask(function*(){
            try {
                yield navigator.clipboard.writeText(props.value);
                set_copied(true);
                copy_timeout.current = setTimeout(()=>set_copied(false), 2000);
            } catch(e){
                console.error('Copy failed:', e);
            }
        });
    };
  return <Basic_input
    {...rest_props}
    is_error={is_error}
    cls={cn(styles.text_input, props.cls,
      props.input_prefix && styles.with_prefix)}
    is_disabled={props.disabled}
    slot_left={props.input_prefix
      ? <span className={styles.input_prefix}>{props.input_prefix}</span>
      : null}
    slot_right={<Fragment>
      {has_error_icon && <div className={styles.error_icon}>
        {error_icon}</div>}
      {has_hint_icon && <div className={styles.hint_icon}>
        {hint_icon}</div>}
      {<div
        className={cn(styles.copy_icon, props.copy_icon_cls)}
        onClick={e=>{
          prevent_default_copy ? on_copy(e) : handle_copy();
        }}
        title={copied ? 'Copied!' : 'Copy'}><Icon_copy/></div>}
        {copied && <p className={styles.copy_tooltip}><Icon_check/> Copied!</p>}
    </Fragment>}
    slot_bottom={props.slot_bottom || <Fragment>
      {has_error && <div className={styles.error}>{error}</div>}
      {has_hint && <div className={styles.hint}>{hint}</div>}
    </Fragment>}
  />;
};
interface i_password_input_props extends i_text_input_props {
    always_show_pass_meter?: boolean;
    pass_meter?: t_render_prop;
}
export const Password_input = (props: i_password_input_props)=>{
    const {pass_meter, always_show_pass_meter, ...rest_props} = props;
    const [pass_shown, set_pass_shown] = useState(false);
    const icon = pass_shown
        ? default_icons.pass_shown
        : default_icons.pass_hidden;
    const on_toggle = ()=>{ !props.is_disabled && set_pass_shown(v=>!v); };
    const show_pass_meter = always_show_pass_meter ||
        props.value && pass_meter && !props.is_error;
    return <Text_input
      {...rest_props}
      cls={styles.password_input}
      type={pass_shown ? 'text' : 'password'}
      hint_icon={<div onClick={on_toggle}>{icon}</div>}
      error_icon={null}
      with_hint
      slot_bottom={show_pass_meter ? pass_meter(props.value) : undefined}
    />;
};
interface i_itput_with_select_interface {
    input_value: string;
    select_value: string;
    options: Array<i_basic_select_option>;
    on_change: (value?: string)=>void;
    on_select: (value: string | number | null)=>void;
    slot_left?: JSX.Element | t_render_prop;
    select_cls?: string;
    input_cls?: string;
}
export const Input_with_select = (props: i_itput_with_select_interface)=>{
  const {slot_left, select_value, input_value, options, select_cls, input_cls,
      on_change, on_select} = props;
  const {dropdown_icon, selected_icon} = get_config();
  return (
    <Basic_input
      value={input_value}
      on_change={on_change}
      cls={input_cls}
      slot_left={slot_left}
      slot_right={
        <Basic_select
          value={select_value}
          options={options}
          on_change={on_select}
          cls={cn(styles.input_with_select, select_cls)}
          render_body={({value})=>
            <>
              {options.find(o=>o.value === value)?.label}
              <span data-elem="dropdown_icon">{dropdown_icon}</span>
            </>}
          render_option={({option, selected})=>
            <>
              {option.label}
              {selected &&
                <span data-elem="selected_icon">{selected_icon}</span>}
            </>}
        />
      }
    />
  );
};
